"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronDown, Mail, Menu, Phone, X } from "lucide-react";

import { mainNav, primaryCta, type NavItem } from "@/config/navigation";
import { site } from "@/config/site";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

function Brand({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <Link
      href="/"
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-md"
      aria-label={`${site.name} – úvodní stránka`}
    >
      <Image
        src={site.brand.logo}
        alt={site.brand.logoAlt}
        width={44}
        height={44}
        priority
        className="h-11 w-11 shrink-0 object-contain"
      />
      <span className="flex flex-col leading-tight">
        <span className="font-serif text-lg font-bold text-navy-800">
          GP tax consulting
        </span>
        <span className="text-xs tracking-wide text-ink">
          Účetnictví · Daně · Mzdy
        </span>
      </span>
    </Link>
  );
}

function DesktopNav({ pathname }: { pathname: string }) {
  return (
    <ul className="hidden items-center gap-1 lg:flex">
      {mainNav.map((item) => {
        const active = isActive(pathname, item.href);
        if (item.children) {
          return (
            <li key={item.href} className="group relative">
              <Link
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "inline-flex min-h-[44px] items-center gap-1 rounded-md px-3 text-[0.95rem] font-medium transition-colors",
                  active
                    ? "text-navy-800"
                    : "text-ink hover:bg-navy-50 hover:text-navy-800",
                )}
              >
                {item.label}
                <ChevronDown
                  className="h-4 w-4 transition-transform group-hover:rotate-180"
                  aria-hidden="true"
                />
              </Link>
              <ul className="invisible absolute left-0 top-full z-50 w-72 translate-y-1 rounded-lg border border-line bg-white p-2 opacity-0 shadow-[var(--shadow-card)] transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                {item.children.map((child: NavItem) => (
                  <li key={child.href}>
                    <Link
                      href={child.href}
                      className="block rounded-md px-3 py-2.5 transition-colors hover:bg-navy-50 focus-visible:bg-navy-50"
                    >
                      <span className="block text-[0.95rem] font-semibold text-navy-800">
                        {child.label}
                      </span>
                      {child.description ? (
                        <span className="mt-0.5 block text-sm text-ink">
                          {child.description}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          );
        }
        return (
          <li key={item.href}>
            <Link
              href={item.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "inline-flex min-h-[44px] items-center rounded-md px-3 text-[0.95rem] font-medium transition-colors",
                active
                  ? "text-navy-800"
                  : "text-ink hover:bg-navy-50 hover:text-navy-800",
              )}
            >
              {item.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function MobileNav({
  pathname,
  onClose,
}: {
  pathname: string;
  onClose: () => void;
}) {
  return (
    <div className="lg:hidden">
      <ul className="flex flex-col gap-1 border-t border-line pt-4">
        {mainNav.map((item) => (
          <li key={item.href}>
            <Link
              href={item.href}
              onClick={onClose}
              aria-current={isActive(pathname, item.href) ? "page" : undefined}
              className={cn(
                "flex min-h-[44px] items-center rounded-md px-3 font-semibold",
                isActive(pathname, item.href)
                  ? "bg-navy-50 text-navy-800"
                  : "text-navy-800 hover:bg-navy-50",
              )}
            >
              {item.label}
            </Link>
            {item.children ? (
              <ul className="ml-3 border-l border-line pl-3">
                {item.children.map((child) => (
                  <li key={child.href}>
                    <Link
                      href={child.href}
                      onClick={onClose}
                      className="flex min-h-[44px] items-center rounded-md px-3 text-[0.95rem] text-ink hover:bg-navy-50 hover:text-navy-800"
                    >
                      {child.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
        <Button href={primaryCta.href} className="w-full" onClick={onClose}>
          {primaryCta.label}
        </Button>
        <a
          href={`tel:${site.contact.phoneHref}`}
          className="flex min-h-[44px] items-center gap-2 px-1 text-navy-800"
          onClick={onClose}
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          {site.contact.phone}
        </a>
        <a
          href={`mailto:${site.contact.email}`}
          className="flex min-h-[44px] items-center gap-2 px-1 text-navy-800"
          onClick={onClose}
        >
          <Mail className="h-4 w-4" aria-hidden="true" />
          {site.contact.email}
        </a>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/95 backdrop-blur">
      <div className="bg-navy-900 text-navy-100">
        <div className="content-container flex flex-wrap items-center justify-between gap-x-6 gap-y-1 py-2 text-[0.8rem]">
          <p>
            <span className="text-brass-100">Pracovní doba:</span>{" "}
            {site.contact.officeHours} {site.contact.officeHoursNote}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <a
              href={`tel:${site.contact.phoneHref}`}
              className="flex items-center gap-1.5 hover:text-white"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              {site.contact.phone}
            </a>
            <a
              href={`mailto:${site.contact.email}`}
              className="flex items-center gap-1.5 hover:text-white"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              {site.contact.email}
            </a>
          </div>
        </div>
      </div>

      <div className="content-container flex items-center justify-between gap-4 py-3">
        <Brand />

        <nav aria-label="Hlavní navigace">
          <DesktopNav pathname={pathname} />
        </nav>

        <div className="hidden lg:block">
          <Button href={primaryCta.href}>{primaryCta.label}</Button>
        </div>

        <button
          ref={toggleRef}
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          aria-label={mobileOpen ? "Zavřít menu" : "Otevřít menu"}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line text-navy-800 lg:hidden"
        >
          {mobileOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>

      {mobileOpen ? (
        <div id={panelId} className="content-container pb-6 lg:hidden">
          <MobileNav
            pathname={pathname}
            onClose={() => setMobileOpen(false)}
          />
        </div>
      ) : null}
    </header>
  );
}
